import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import root from 'window-or-global';

const isNumberChar = input => !!(input && input.match(/[0-9.]/));

function NumberInput({
    value,
    onValueChange,
    allowDecimal,
    decimalPlaces,
    showGroupSeparator,
    maxIntegerDigits,
    ...props
}) {
    const inputEl = useRef(null);
    const notInitial = useRef(false);
    const [selectedRange, setSelectedRange] = useState([0, 0]);
    const [lastKey, setLastKey] = useState(null);

    const unformatValue = input => input.replace(/,/g, '');

    const formatValue = input => {
        const [whole, decimal] = unformatValue(input).split('.');
        return (
            (showGroupSeparator ? Number(whole).toLocaleString('en') : whole) +
            (decimal !== undefined ? `.${decimal.padEnd(decimalPlaces, '0')}` : '')
        );
    };

    const [displayValue, setDisplayValue] = useState(
        value === '' ? '' : formatValue(value.toString())
    );

    const spliceChar = (input, at, char, slice = 1) =>
        input.substr(0, at) + char + input.substr(at + slice);

    useEffect(() => {
        if (notInitial.current) {
            onValueChange(unformatValue(displayValue), displayValue);
        } else {
            notInitial.current = true;
        }
    }, [displayValue]);

    const handleChange = event => {
        let action = event.nativeEvent.inputType;
        let newChar = event.nativeEvent.data;

        // Translate basic input events
        if (event.nativeEvent.type === 'input' && lastKey != null) {
            if (isNumberChar(lastKey)) {
                newChar = lastKey;
                action = 'insertText';
            } else if (lastKey === 'Backspace') {
                action = 'deleteContentBackward';
            }
            setLastKey(null);
        }

        const cursorPos = event.target.selectionStart;
        const decimalPos = displayValue.indexOf('.');

        // This function moves the cursor (caret) to where the user expects before the next paint.
        const setCursor = (pos, absolute) => {
            root.requestAnimationFrame(() => {
                const newPos = absolute ? pos : cursorPos + pos - 1;
                inputEl.current.selectionStart = newPos;
                inputEl.current.selectionEnd = newPos;
            });
        };

        // Handle other change events not fired from keyboard input (e.g. autocomplete selection)
        if (action === undefined) {
            setDisplayValue(formatValue(event.target.value));
            return;
        }

        if (action === 'insertText' || action === 'insertCompositionText') {
            if (!isNumberChar(newChar)) {
                // Although we're blocking these characters in the keyDown event, that
                // event won't fire from software keyboards on mobile devices.
                setCursor(0);
                return;
            }

            if (newChar === '.' && allowDecimal) {
                // User is starting value with a decimal point
                if (decimalPos === -1 && displayValue === '') {
                    setDisplayValue('0.00');
                    setCursor(2);
                    return;
                }

                // User is appending a decimal point to the end
                if (cursorPos === displayValue.length + 1) {
                    setDisplayValue(formatValue(`${displayValue}.`));
                    setCursor(1);
                    return;
                }

                // User is typing through the existing decimal point
                if (cursorPos - 1 === decimalPos) {
                    setCursor(1);
                    return;
                }
            }

            if (newChar !== '.') {
                // User typed a digit with text selected: replace the selected text
                if (selectedRange[0] !== selectedRange[1]) {
                    const size = selectedRange[1] - selectedRange[0];
                    const newValue = formatValue(
                        spliceChar(displayValue, cursorPos - 1, newChar, size)
                    );
                    setDisplayValue(newValue);

                    // Put the cursor after the new character
                    // (We're basically preserving the cursor distance from the end of the original value)
                    setCursor(
                        newValue.length - (displayValue.length - Math.max(...selectedRange)),
                        true
                    );
                    return;
                }

                // User typed a digit after the decimal point: overwrite existing digit
                if (decimalPos !== -1 && cursorPos - 1 > decimalPos) {
                    if (cursorPos > displayValue.length) {
                        // Prevent adding more digits after the last
                        return;
                    }

                    setDisplayValue(spliceChar(displayValue, cursorPos - 1, newChar));
                    setCursor(1);
                    return;
                }

                // User is inserting new integer digit at cursor
                if (unformatValue(displayValue).split('.')[0].length < maxIntegerDigits) {
                    const newValue = formatValue(
                        spliceChar(displayValue, cursorPos - 1, newChar, 0)
                    );
                    setDisplayValue(newValue);
                    setCursor(newValue.length - displayValue.length);
                    return;
                }
            }
        }

        if (action === 'deleteContentBackward') {
            // User pressed backspace with characters selected: delete the selected characters
            if (selectedRange[0] !== selectedRange[1]) {
                const size = selectedRange[1] - selectedRange[0];

                if (size === displayValue.length) {
                    setDisplayValue('');
                    return;
                }

                const newValue = formatValue(spliceChar(displayValue, cursorPos, '', size));
                setDisplayValue(newValue);
                setCursor(
                    newValue.length - (displayValue.length - Math.max(...selectedRange)),
                    true
                );
                return;
            }

            // User pressed backspace within integer part of number
            if (cursorPos < decimalPos || decimalPos === -1) {
                if (displayValue.length === 1) {
                    setDisplayValue('');
                    return;
                }

                const newValue = formatValue(spliceChar(displayValue, cursorPos, ''));
                setDisplayValue(newValue);
                setCursor(newValue.length - displayValue.length + 2);
                return;
            }

            if (decimalPos !== -1) {
                // User pressed backspace on the last decimal digit
                if (cursorPos === displayValue.length - 1) {
                    setDisplayValue(spliceChar(displayValue, cursorPos, '0'));
                    setCursor(1);
                    return;
                }

                // User pressed backspace on a decimal digit that wasn't the last
                if (cursorPos > decimalPos) {
                    setDisplayValue(formatValue(spliceChar(displayValue, cursorPos, '')));
                    setCursor(1);
                    return;
                }

                // User pressed backspace on the decimal point: delete the whole decimal part
                if (cursorPos === decimalPos) {
                    setDisplayValue(spliceChar(displayValue, cursorPos, '', decimalPlaces + 1));
                    setCursor(1);
                    return;
                }
            }
        }

        // If nothing matched, do nothing and keep the cursor where it was.
        setCursor(0);
    };

    const handleKeyDown = ({ nativeEvent: e }) => {
        // We'll lose the selection range by the time we check in handleChange, so let's store it here.
        setSelectedRange([e.target.selectionStart, e.target.selectionEnd]);

        // Supress keystokes that aren't helpful to making or editing a number
        if (e.key && e.key.length === 1 && !isNumberChar(e.key) && !e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            return;
        }

        // Supress a keystoke we don't feel like implementing
        if (e.key && e.key === 'Delete') {
            e.preventDefault();
            return;
        }

        setLastKey(e.key);
    };

    return (
        <input
            {...props}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            ref={inputEl}
            value={displayValue}
        />
    );
}

NumberInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onValueChange: PropTypes.func.isRequired,
    allowDecimal: PropTypes.bool,
    showGroupSeparator: PropTypes.bool,
    decimalPlaces: PropTypes.number,
    maxIntegerDigits: PropTypes.number,
};

NumberInput.defaultProps = {
    value: '',
    allowDecimal: true,
    showGroupSeparator: true,
    decimalPlaces: 2,
    maxIntegerDigits: 9,
};

export default NumberInput;
